import { formatDateTimeSinceNow } from "@/lib/Formatters/formatDateTimeSinceNow";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { formatDateTimeLocal } from "@/lib/Formatters/formatDateTime";
import dayjs from "dayjs";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { useTranslations } from "use-intl";
import { useSharedNow } from "@/lib/Hooks/useSharedNow";

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

type CompactUnit = {
  key: "justNow" | "minutes" | "hours" | "days";
  count: number;
};

function getCompactUnit(minutes: number): CompactUnit {
  if (minutes < 1) {
    return { key: "justNow", count: 0 };
  }

  if (minutes < 60) {
    return { key: "minutes", count: minutes };
  }

  if (minutes < 60 * 24) {
    return { key: "hours", count: Math.floor(minutes / 60) };
  }

  return { key: "days", count: Math.floor(minutes / (60 * 24)) };
}

export function TimeSinceNow({
  time,
  className,
  compact = false,
}: {
  time: string;
  className?: string;
  compact?: boolean;
}) {
  const t = useTranslations("common.timeSinceNow");
  const minutes = time ? dayjs().diff(dayjs.utc(time), "minute") : 0;

  // Under an hour the label moves every minute; past that only the hour digit
  // can change, so tick hourly instead.
  useSharedNow(minutes < 60 ? MINUTE_MS : HOUR_MS);

  if (!time) {
    return <span className={cn("text-gray-500", className)}>--</span>;
  }

  const unit = getCompactUnit(minutes);

  return (
    <Tooltip>
      <TooltipTrigger
        className={cn(
          `inline-flex cursor-pointer items-center gap-1.5 rounded-md p-1`,
          className,
        )}
      >
        <time className="text-gray-500">
          {compact
            ? t(unit.key, { count: unit.count })
            : formatDateTimeSinceNow(time)}
        </time>
      </TooltipTrigger>
      <TooltipPrimitive.Portal>
        <TooltipContent className="p-3">
          <div className="mb-1 flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide">
              {t("local")} ({dayjs.tz.guess()})
            </span>
          </div>
          <time className="font-mono text-sm">{formatDateTimeLocal(time)}</time>

          <div className="pt-3">
            <div className="mb-1 flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide">
                {t("utc")}
              </span>
            </div>
            <time className="font-mono text-sm">
              {dayjs(time).utc().format("DD MMM YYYY, hh:mma")}
            </time>
          </div>
        </TooltipContent>
      </TooltipPrimitive.Portal>
    </Tooltip>
  );
}
